import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const STEPS = [
  {
    n: "01",
    title: "Sourcing",
    body:
      "Season-timed visits to estates in the Western Ghats. Lots are held for us at the pod-splitter, not the auction floor.",
  },
  {
    n: "02",
    title: "Grading",
    body:
      "Size, colour, moisture and volatile oil. Anything that doesn't hit spec goes back — not into another bag.",
  },
  {
    n: "03",
    title: "Packaging",
    body:
      "Vacuum, jute-lined cartons, or private-label film. Palletized and marked to your importer's playbook.",
  },
  {
    n: "04",
    title: "Delivery / Export",
    body:
      "FOB Chennai / Krishnapatnam or DAP to your warehouse. Docs, phyto, COO — handled.",
  },
];

export const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const lineScale = useTransform(scrollYProgress, [0.05, 0.7], [0, 1]);

  return (
    <section
      id="process"
      data-testid="process-section"
      className="relative bg-cream py-28 md:py-40"
      ref={ref}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-sans text-[11px] uppercase tracking-[0.32em] text-charcoal/60 mb-6 flex items-center gap-3"
        >
          <span className="text-gold">§</span> The Process
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
          className="font-serif font-light text-charcoal text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-[16ch]"
        >
          Estate <span className="italic text-forest">to</span> container,
          <br /> in four <span className="italic text-forest">measured</span>{" "}
          steps.
        </motion.h2>

        {/* Stepper */}
        <div className="relative mt-20 md:mt-28">
          {/* Animated progress line */}
          <div className="pointer-events-none absolute left-0 right-0 top-[46px] hidden md:block">
            <div className="relative h-[1px] bg-charcoal/15">
              <motion.div
                style={{ scaleX: lineScale, transformOrigin: "left" }}
                className="absolute inset-0 bg-gold"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-10 md:gap-8">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.9,
                  delay: i * 0.12,
                  ease: [0.65, 0, 0.35, 1],
                }}
                className="relative"
                data-testid={`process-step-${s.n}`}
              >
                <div className="flex items-center gap-4 mb-8">
                  <span className="relative z-10 w-[52px] h-[52px] rounded-full bg-cream border border-charcoal/20 flex items-center justify-center font-serif italic text-forest text-xl">
                    {s.n}
                  </span>
                  <span className="md:hidden h-[1px] flex-1 bg-charcoal/15" />
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-charcoal mb-3">
                  {s.title}
                </h3>
                <p className="font-sans text-charcoal/65 text-sm md:text-[15px] leading-relaxed max-w-[32ch]">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
